import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { X, Laptop, AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { cn } from '@/lib/utils';
import api from '@/services/api';

interface AssignLoanerModalProps {
  open: boolean;
  onClose: () => void;
  repairId: string;
  onSuccess: () => void;
}

export default function AssignLoanerModal({ open, onClose, repairId, onSuccess }: AssignLoanerModalProps) {
  const queryClient = useQueryClient();
  
  const [loanerAssetId, setLoanerAssetId] = useState('');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Fetch available loaner assets
  const { data: loanerAssetsData, isLoading: loadingAssets } = useQuery({
    queryKey: ['loanerAssets'],
    queryFn: async () => {
      // Get available assets that can be used as loaners
      const response = await api.get('/assets?status=AVAILABLE&limit=100');
      return response.data.data || [];
    },
    enabled: open,
  });

  const loanerAssets = loanerAssetsData || [];

  // Assign loaner mutation
  const assignMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        loanerAssetId,
        notes,
      };
      const response = await api.post(`/repairs/${repairId}/assign-loaner`, payload);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['repair', repairId] });
      queryClient.invalidateQueries({ queryKey: ['repairs'] });
      queryClient.invalidateQueries({ queryKey: ['loanerAssets'] });
      resetForm();
      onSuccess();
    },
    onError: (error: any) => {
      setError(error.response?.data?.message || 'Failed to assign loaner asset');
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!loanerAssetId) {
      setError('Please select a loaner asset');
      return;
    }

    assignMutation.mutate();
  };

  const resetForm = () => {
    setLoanerAssetId('');
    setNotes('');
    setError(null);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative bg-background rounded-lg shadow-xl w-full max-w-md m-4">
        <Card className="border-0">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4 border-b">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Laptop className="w-5 h-5" />
                Assign Loaner Asset
              </CardTitle>
              <CardDescription>
                Provide a temporary replacement during repair
              </CardDescription>
            </div>
            <Button variant="ghost" size="icon" onClick={handleClose}>
              <X className="w-5 h-5" />
            </Button>
          </CardHeader>
          
          <CardContent className="pt-6">
            {/* Error Alert */}
            {error && (
              <div className="flex items-center gap-2 p-3 mb-4 rounded-md bg-destructive/10 border border-destructive/20">
                <AlertCircle className="w-5 h-5 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Loaner Asset Selection */}
              <div className="space-y-2">
                <Label htmlFor="loanerAssetId">
                  Loaner Asset <span className="text-destructive">*</span>
                </Label>
                {loadingAssets ? (
                  <div className="flex items-center justify-center py-4">
                    <div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  </div>
                ) : loanerAssets.length === 0 ? (
                  <div className="p-4 rounded-md bg-yellow-50 border border-yellow-200">
                    <p className="text-sm text-yellow-800">
                      No available loaner assets found. Please check asset availability.
                    </p>
                  </div>
                ) : (
                  <select
                    id="loanerAssetId"
                    className={cn(
                      "w-full h-10 px-3 rounded-md border bg-background text-sm",
                      !loanerAssetId && error ? "border-destructive" : "border-input"
                    )}
                    value={loanerAssetId}
                    onChange={(e) => setLoanerAssetId(e.target.value)}
                  >
                    <option value="">Select a loaner asset</option>
                    {loanerAssets.map((asset: any) => (
                      <option key={asset.id} value={asset.id}>
                        {asset.assetTag} - {asset.name}
                        {asset.brand && ` (${asset.brand.name})`}
                      </option>
                    ))}
                  </select>
                )}
                <p className="text-xs text-muted-foreground">
                  The employee will be assigned this asset temporarily until their original asset is repaired.
                </p>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes (Optional)</Label>
                <textarea
                  id="notes"
                  className="w-full min-h-[80px] p-3 rounded-md border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Any special notes about the loaner assignment..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {/* Info Box */}
              <div className="p-4 rounded-md bg-blue-50 border border-blue-200">
                <p className="text-sm text-blue-800">
                  <strong>Note:</strong> Assigning a loaner will:
                </p>
                <ul className="text-sm text-blue-700 mt-2 list-disc list-inside space-y-1">
                  <li>Mark the loaner asset as assigned</li>
                  <li>Record the issue date and time</li>
                  <li>Link the loaner to this repair record</li>
                </ul>
              </div>

              {/* Actions */}
              <div className="flex items-center justify-end gap-3 pt-4 border-t">
                <Button type="button" variant="outline" onClick={handleClose}>
                  Cancel
                </Button>
                <Button 
                  type="submit" 
                  disabled={assignMutation.isPending || loanerAssets.length === 0}
                >
                  {assignMutation.isPending ? 'Assigning...' : 'Assign Loaner'}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
